import { SimpleGrid, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { UserDto } from "doxecure-commons/doxecureClientTypes";
import { ProductType, WaitingRequestAction } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { SeaShellBox } from "@/components/SeaShellBox";
import { InvoicesCard } from "@components/SubcriptionCards/InvoicesCard";
import { MandateCard }  from "@components/SubcriptionCards/MandateCard";
import { PlanCard }     from "@components/SubcriptionCards/PlanCard";
import { UsersCard }    from "@components/SubcriptionCards/UsersCard";
import { Mandate } from "@components/SubcriptionCards/types";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import useProductList from "@/hooks/useProductList";
import { useSubscription } from "@hooks/useSubscription";
import { createCBMandate, updateMandate } from "@services/users.service";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";

interface SubscriptionTabProps {
    connectedUser?: Nullable<UserDto>;
    pricePerExtraUser?: number;
}

export const SubscriptionTab = (props: SubscriptionTabProps) => {
    const { connectedUser, pricePerExtraUser } = props;
    const { data: activeSubscription, requestCancelSubscription, cancelTerminationRequest, startSubscriptionImmediately, buyExtraPack } = useSubscription();
    const { data: productsList } = useProductList(true);
    const { mollie, ready } = useMollie();
    const [mandate, setMandate] = useState<Nullable<Mandate>>(connectedUser?.corporations?.first()?.mandate as Mandate);
    const [pendingToast, setPendingToast] = useState(false);
    const toast = useToast();

    const corporation           = connectedUser?.corporations?.first();
    const isTrialPeriod         = $ok(corporation?.trialPeriod?.end);
    const waitingRequestAction  = activeSubscription?.requestWaintingActionInfos?.type as WaitingRequestAction;
    const maxUsers              = isTrialPeriod ? corporation?.trialPeriod?.extraUsers ?? 1 : corporation?.maxUsers ?? -1;
    const productPack           = productsList?.packs.filter(p => p.type === ProductType.Pack).first();

    const handleCardSubmit = async (cbToken: string): Promise<void> => {
        const checkoutURL = await createCBMandate(corporation!.apid, cbToken);
        if ($ok(checkoutURL)) {
            window.location.href = checkoutURL!;
        }
    };
    
    const handleMandateSubmit = async (consumerAccount: string, consumerName: string): Promise<void> => {
        const corpDTO = await updateMandate(corporation!.apid, consumerName, consumerAccount);
        if ($ok(corpDTO)) { 
            setMandate(corpDTO.mandate);
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat SEPA"));
        }
    };

    function handleChangeToUnlimitedPlan(): void {
        try {

        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la demande de passer en illimité"));
        }
    }

    const handleStartNow = async () => {
        try {
            await startSubscriptionImmediately.mutateAsync();
            setPendingToast(true);
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de l'activation de votre abonnement"));
        }
    }

    const handleTerminate = async () => {
        try {
            await requestCancelSubscription.mutateAsync();
            toast(defaultSuccessToast("La demande de résiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la résiliation de votre abonnement"));
        }
    }

    const handleCancelTerminationRequest = async() => {
        try {
            await cancelTerminationRequest.mutateAsync();
            toast(defaultSuccessToast("L'annulation de la résiliation a bien été prise en compte"));
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation de la résiliation"));
        }
    }

    const handleBuyExtractPack = async () => {
        try {
            await buyExtraPack.mutateAsync();
        } catch (e) {
            toast(defaultErrorToast("Une erreur est survenue lors de l'achat d'un pack de signatures"));
        }
    }

    function handleAddUsers(count: number): void {
    }

    function handleRemoveUsers(count: number): void {
    }

    useEffect(() => {
        if (!$ok(activeSubscription) || !pendingToast || startSubscriptionImmediately.isLoading) { return; }

        if ($ok(corporation?.mandate?.cardNumber)) {
            toast(defaultSuccessToast(`Votre abonnement a bien démarré. Vous avez un crédit de ${activeSubscription?.signatureCount.subscriptionMax} signatures`));
        } else {
            toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de démarrer plus tôt votre abonnement"));
        }
        setPendingToast(false);
    }, [activeSubscription, pendingToast, startSubscriptionImmediately.isLoading]);

    return (
        <SeaShellBox component="form" px={4} py={10}>
            <SimpleGrid columns={{ base: 1, lg: 2}} spacing={5} mb={5}>
                <PlanCard
                    connectedUser={connectedUser}
                    subscription={activeSubscription}
                    waitingRequestAction={waitingRequestAction}
                    extraUsers={0}
                    pricePerExtraUser={pricePerExtraUser ?? 0}
                    productPackInfos={productPack}
                    onBuyExtraPack={handleBuyExtractPack}
                    onCancelTerminationRequest={handleCancelTerminationRequest}
                    onChangeToUnlimitedPlan={handleChangeToUnlimitedPlan}
                    onStartNow={handleStartNow}
                    onTerminate={handleTerminate}
                />
                <UsersCard
                    activeUsers={1}
                    maxUsers={maxUsers}
                    isTrialPeriod={isTrialPeriod}
                    pricePerExtraUser={pricePerExtraUser ?? 0}
                    onAddUsers={handleAddUsers}
                    onRemoveUsers={handleRemoveUsers}
                />
                <MandateCard
                    mandate={mandate}
                    isTerminationRequested={waitingRequestAction === "cancel-subscription"}
                    companyName={config.app.title}
                    testMode={config.app.mollie.testmode}
                    mollie={mollie}
                    mollieReady={ready}
                    onCardSubmit={handleCardSubmit}
                    onMandateSubmit={handleMandateSubmit}
                />
            </SimpleGrid>

            <InvoicesCard />
        </SeaShellBox>
    );
};